// The rest of "Make it my own": the built-in widgets beyond the number, the chart, the goal and the list, each as a
// widget of the user's own in the widget kit, with the same settings and the same data, so it can be changed
// freely. A timer becomes the kit's focus timer with its own minutes; a note or a checklist keeps what is in it.
// The widgets that read the user's computer (Agent, Needs you and the rest) and links stay built in: a widget of
// one's own cannot reach the computer, and opens only its data's own links.

import { STICKY_COLORS } from "./local";
import { WIDGET_EXAMPLE } from "../../shared/widgetExamples";
import type { WidgetItem } from "../../shared/types";

// a value written into the script: JSON, with every < escaped, so nothing in it can end the script it sits in
const js = (v: unknown) => JSON.stringify(v).replace(/</g, "\\u003c");
const num = (v: unknown, d: number, lo: number, hi: number) => (typeof v === "number" && Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : d);
const str = (v: unknown, n = 4000) => (typeof v === "string" ? v.slice(0, n) : "");

const FOCUS_START = "var FOCUS = 25 * 60, REST = 5 * 60;";

/** The code for one of these kinds, or null for a kind that stays built in. */
export function moreForks(w: WidgetItem): string | null {
  const p = w.props ?? {};
  switch (w.type) {
    case "timer": {
      const html = WIDGET_EXAMPLE.focus?.html ?? "";
      if (!html.includes(FOCUS_START)) return null;
      const work = Math.round(num(p.work, 25, 1, 240));
      const rest = Math.round(num(p.rest, 5, 1, 120));
      return html.replace(FOCUS_START, `// minutes of focus, then of rest\n  var FOCUS = ${js(work)} * 60, REST = ${js(rest)} * 60;`);
    }
    case "map":
      return WIDGET_EXAMPLE.map?.html ?? null;
    case "clock":
      return clock(p.face === "digital" ? "digital" : "analog", str(p.tz, 60), p.seconds !== false);
    case "countdown":
      return countdown(str(p.to, 40), str(p.label, 80) || "Launch");
    case "notes":
      return notes(str(p.text, 12000));
    case "sticky": {
      const named = String(p.color ?? "butter");
      const [bg, fg] = Object.hasOwn(STICKY_COLORS, named) ? STICKY_COLORS[named] : STICKY_COLORS.butter;
      return sticky(str(p.text, 4000), bg, fg);
    }
    case "tasks": {
      const tasks = (Array.isArray(p.tasks) ? p.tasks : []).slice(0, 100).flatMap((t, i) => (t && typeof t === "object" && typeof (t as { text?: unknown }).text === "string" ? [{ id: String((t as { id?: unknown }).id ?? `t${i}`).slice(0, 40), text: str((t as { text: string }).text, 300), done: !!(t as { done?: unknown }).done }] : []));
      return tasksHtml(tasks);
    }
    case "heading":
      return heading(str(p.text, 200) || "This week", p.align === "center" ? "center" : p.align === "right" ? "right" : "left");
    case "table":
      return table(Math.round(num(p.max, 20, 1, 200)));
    case "status":
      return statusHtml();
    case "feed":
      return p.source === "stream" ? feed(Math.round(num(p.max, 30, 1, 200))) : null;
    case "brief":
      return brief();
    default:
      return null;
  }
}

function clock(face: "analog" | "digital", tz: string, seconds: boolean): string {
  return `<div class="cb-fill cb-center" id="root">
  <svg id="face" viewBox="0 0 100 100" aria-hidden="true">
    <circle cx="50" cy="50" r="46" style="fill:var(--field);stroke:var(--line)" stroke-width="1"/>
    <g id="ticks"></g>
    <line id="h" x1="50" y1="50" x2="50" y2="27" style="stroke:var(--fg)" stroke-width="4" stroke-linecap="round"/>
    <line id="m" x1="50" y1="50" x2="50" y2="15" style="stroke:var(--fg)" stroke-width="2.6" stroke-linecap="round"/>
    <line id="s" x1="50" y1="56" x2="50" y2="12" style="stroke:var(--accent)" stroke-width="1.2" stroke-linecap="round"/>
    <circle cx="50" cy="50" r="2.6" style="fill:var(--accent)"/>
  </svg>
  <div class="cb-stack cb-center" id="digital" style="--gap:0.25rem">
    <span class="cb-value" id="time"></span>
    <span class="cb-label" id="date"></span>
  </div>
</div>
<style>
  #face { width: min(100%, 100vh); height: min(100%, 100vw); max-height: 100%; }
</style>
<script>
  // "analog" or "digital", the time zone (empty for this computer's), and whether the seconds show
  var FACE = ${js(face)};
  var TIME_ZONE = ${js(tz)};
  var SECONDS = ${js(seconds)};
  var zone = TIME_ZONE || undefined;
  var ticks = "";
  for (var i = 0; i < 12; i++) ticks += '<line x1="50" y1="8" x2="50" y2="' + (i % 3 ? 11 : 14) + '" style="stroke:var(--fg-3)" stroke-width="' + (i % 3 ? 1 : 2) + '" transform="rotate(' + i * 30 + ' 50 50)"/>';
  document.getElementById("ticks").innerHTML = ticks;
  // an svg has no hidden switch of its own, so both go by their style
  document.getElementById("face").style.display = FACE === "analog" ? "" : "none";
  document.getElementById("digital").style.display = FACE === "analog" ? "none" : "";
  document.getElementById("s").style.display = SECONDS ? "" : "none";
  function now() {
    var parts = {};
    try {
      new Intl.DateTimeFormat("en-GB", { timeZone: zone, hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23" }).formatToParts(new Date()).forEach(function (x) { parts[x.type] = Number(x.value); });
    } catch (e) {
      var d = new Date();
      parts = { hour: d.getHours(), minute: d.getMinutes(), second: d.getSeconds() };
    }
    return parts;
  }
  function draw() {
    var t = now();
    if (FACE === "analog") {
      document.getElementById("h").setAttribute("transform", "rotate(" + ((t.hour % 12) * 30 + t.minute / 2) + " 50 50)");
      document.getElementById("m").setAttribute("transform", "rotate(" + (t.minute * 6 + t.second / 10) + " 50 50)");
      document.getElementById("s").setAttribute("transform", "rotate(" + t.second * 6 + " 50 50)");
    } else {
      var opts = { timeZone: zone, hour: "numeric", minute: "2-digit" };
      if (SECONDS) opts.second = "2-digit";
      var d = new Date();
      try {
        document.getElementById("time").textContent = d.toLocaleTimeString(undefined, opts);
        document.getElementById("date").textContent = d.toLocaleDateString(undefined, { timeZone: zone, weekday: "long", day: "numeric", month: "long" });
      } catch (e) {
        document.getElementById("time").textContent = d.toLocaleTimeString();
      }
    }
  }
  draw();
  setInterval(draw, 1000);
</script>`;
}

function countdown(to: string, label: string): string {
  return `<div class="cb-fill cb-center" style="--gap:0.4rem">
  <div class="cb-inline" id="parts" style="--gap:1rem;justify-content:center"></div>
  <span class="cb-label" id="what"></span>
</div>
<style>
  .part { display: grid; justify-items: center; }
  .part b { font-size: 2.2rem; }
  [data-size="s"] .part b { font-size: 1.5rem; }
</style>
<script>
  // the moment it counts down to (as the date picker writes it, on this computer's clock), and what it is
  var TO = ${js(to)};
  var LABEL = ${js(label)};
  function draw() {
    var end = TO ? new Date(TO).getTime() : NaN;
    var el = document.getElementById("parts");
    var what = document.getElementById("what");
    if (!isFinite(end)) {
      el.innerHTML = "";
      what.textContent = "Set the date in the code's first lines: TO";
      return;
    }
    var left = Math.max(0, end - Date.now());
    if (!left) {
      el.innerHTML = '<span class="cb-display">' + board.esc(LABEL) + " is here</span>";
      what.textContent = new Date(end).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });
      return;
    }
    var d = Math.floor(left / 864e5), h = Math.floor((left % 864e5) / 36e5), m = Math.floor((left % 36e5) / 6e4), s = Math.floor((left % 6e4) / 1e3);
    var shown = d > 0 ? [[d, d === 1 ? "day" : "days"], [h, "hours"], [m, "min"]] : [[h, "hours"], [m, "min"], [s, "sec"]];
    el.innerHTML = shown.map(function (x) { return '<span class="part"><b class="cb-value">' + x[0] + '</b><span class="cb-sub">' + x[1] + "</span></span>"; }).join("");
    what.textContent = "Until " + LABEL;
  }
  draw();
  setInterval(draw, 1000);
</script>`;
}

/** A note's words in an editable block (words on the page, so the board sees what it says), kept with the widget. */
function editable(first: string, style: string, root = ""): string {
  return `<div class="cb-fill${root ? " " + root : ""}">
  <div id="text" class="cb-grow cb-scroll" spellcheck="true"></div>
  <span class="cb-faint" id="hint">Click to write</span>
</div>
<style>
  #text { outline: 0; white-space: pre-wrap; word-break: break-word; }
  #hint { display: none; }
  #text:empty + #hint { display: block; }
  [data-readonly] #hint { display: none; }
${style}
</style>
<script>
  // what it said when it was made your own; what you write here is kept with the widget
  var FIRST = ${js(first)};
  var box = document.getElementById("text");
  box.textContent = typeof board.state.text === "string" ? board.state.text : FIRST;
  if (!board.readOnly) box.setAttribute("contenteditable", "plaintext-only");
  var timer = null;
  box.addEventListener("input", function () {
    clearTimeout(timer);
    timer = setTimeout(function () { board.save({ text: box.innerText.slice(0, 15000) }); }, 400);
  });
</script>`;
}

function notes(text: string): string {
  return editable(text, "  #text { color: var(--fg); font-size: 0.95rem; line-height: 1.55; }");
}

function sticky(text: string, bg: string, fg: string): string {
  // the note's own paper and ink, as the built-in had them
  return editable(text, `  .note { background: ${bg}; color: ${fg}; border-radius: 10px; padding: 14px 16px; box-shadow: 0 2px 10px rgb(0 0 0 / 0.12); }\n  #text { font-size: 1.15rem; line-height: 1.4; font-weight: 520; }\n  .note #hint { color: inherit; opacity: 0.55; }`, "note");
}

function tasksHtml(tasks: { id: string; text: string; done: boolean }[]): string {
  return `<div class="cb-fill" style="--gap:0.5rem">
  <span class="cb-label" id="left"></span>
  <div class="cb-scroll cb-grow"><div class="cb-stack" id="list" style="--gap:0.15rem"></div></div>
  <form id="add" class="cb-inline" style="--gap:0.4rem"><input id="new" placeholder="Add a task" autocomplete="off"><button class="cb-btn" type="submit">Add</button></form>
</div>
<style>
  .task { display: flex; align-items: center; gap: 0.55rem; padding: 0.3rem 0.1rem; cursor: pointer; }
  .task input { accent-color: var(--accent); width: 1rem; height: 1rem; margin: 0; flex-shrink: 0; }
  .task.is-done span { color: var(--fg-3); text-decoration: line-through; }
  .task .x { margin-left: auto; opacity: 0; border: 0; background: none; color: var(--fg-3); cursor: pointer; font: inherit; }
  .task:hover .x { opacity: 1; }
  #new { flex: 1; min-width: 0; border: 1px solid var(--line); border-radius: 8px; background: var(--field); color: var(--fg); font: inherit; padding: 0.35rem 0.6rem; }
  [data-readonly] #add, [data-readonly] .x { display: none; }
</style>
<script>
  // the tasks when it was made your own; ticks and new tasks are kept with the widget
  var FIRST = ${js(tasks)};
  var tasks = Array.isArray(board.state.tasks) ? board.state.tasks : FIRST;
  function keep() { board.save({ tasks: tasks.slice(0, 200) }); }
  function draw() {
    var open = tasks.filter(function (t) { return !t.done; }).length;
    document.getElementById("left").textContent = tasks.length ? (open ? open + " of " + tasks.length + " to do" : "All done") : "Nothing yet";
    document.getElementById("list").innerHTML = tasks.map(function (t, i) {
      return '<label class="task' + (t.done ? " is-done" : "") + '"><input type="checkbox" data-i="' + i + '"' + (t.done ? " checked" : "") + (board.readOnly ? " disabled" : "") + '><span>' + board.esc(t.text) + '</span><button class="x" type="button" data-x="' + i + '" aria-label="Remove">×</button></label>';
    }).join("");
  }
  document.getElementById("list").addEventListener("change", function (e) {
    var i = Number(e.target.getAttribute("data-i"));
    if (tasks[i]) { tasks[i].done = e.target.checked; keep(); draw(); }
  });
  document.getElementById("list").addEventListener("click", function (e) {
    var x = e.target.getAttribute && e.target.getAttribute("data-x");
    if (x !== null && x !== undefined) { e.preventDefault(); tasks.splice(Number(x), 1); keep(); draw(); }
  });
  document.getElementById("add").addEventListener("submit", function (e) {
    e.preventDefault();
    var input = document.getElementById("new");
    var text = input.value.trim();
    if (!text) return;
    tasks.push({ id: "t" + Date.now().toString(36), text: text.slice(0, 300), done: false });
    input.value = "";
    keep();
    draw();
  });
  document.getElementById("new").addEventListener("keydown", function (e) { e.stopPropagation(); });
  draw();
</script>`;
}

function heading(text: string, align: "left" | "center" | "right"): string {
  return `<div class="cb-fill" style="justify-content:center">
  <span class="cb-display" id="text"></span>
</div>
<style>
  #text { font-size: clamp(1.4rem, 9vh, 3rem); line-height: 1.1; }
</style>
<script>
  // the words, and where they sit: "left", "center" or "right"
  var TEXT = ${js(text)};
  var ALIGN = ${js(align)};
  var el = document.getElementById("text");
  el.textContent = TEXT;
  el.style.textAlign = ALIGN;
</script>`;
}

function table(max: number): string {
  return `<div class="cb-fill" id="main" hidden>
  <div class="cb-scroll cb-grow"><table id="t"></table></div>
</div>
<div class="cb-empty" id="empty"><b>No rows yet</b>They show as soon as its data sends some.</div>
<style>
  table { width: 100%; border-collapse: collapse; font-size: 0.9rem; }
  th { text-align: left; font-weight: 600; color: var(--fg-3); font-size: 0.78rem; padding: 0 0.6rem 0.4rem 0; border-bottom: 1px solid var(--line); }
  td { padding: 0.42rem 0.6rem 0.42rem 0; border-bottom: 1px solid var(--line); color: var(--fg); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 16rem; }
  td.n, th.n { text-align: right; font-variant-numeric: tabular-nums; }
</style>
<script>
  // how many rows it shows
  var MAX = ${js(max)};
  board.onData(function (d) {
    var rows = d.rows || (d.items || []).map(function (i) { return { label: i.label, value: i.value }; });
    rows = rows.slice(0, MAX);
    var has = rows.length > 0;
    document.getElementById("main").hidden = !has;
    document.getElementById("empty").hidden = has;
    if (!has) return;
    var cols = d.columns || Object.keys(rows[0]);
    var isNum = cols.map(function (c) { return rows.every(function (r) { return r[c] === null || r[c] === undefined || typeof r[c] === "number"; }); });
    document.getElementById("t").innerHTML = "<tr>" + cols.map(function (c, i) { return "<th" + (isNum[i] ? ' class="n"' : "") + ">" + board.esc(c) + "</th>"; }).join("") + "</tr>" +
      rows.map(function (r) { return "<tr>" + cols.map(function (c, i) { var v = r[c]; return "<td" + (isNum[i] ? ' class="n"' : "") + ">" + board.esc(v === null || v === undefined ? "" : isNum[i] ? board.fmt(v) : String(v)) + "</td>"; }).join("") + "</tr>"; }).join("");
  });
</script>`;
}

function statusHtml(): string {
  return `<div class="cb-fill" id="main" hidden>
  <span class="cb-label" id="sum"></span>
  <div class="cb-scroll cb-grow"><div class="cb-list" id="rows"></div></div>
</div>
<div class="cb-empty" id="empty"><b>No checks yet</b>Each one shows as soon as its data sends it.</div>
<script>
  board.onData(function (d) {
    var items = d.items || [];
    var has = items.length > 0;
    document.getElementById("main").hidden = !has;
    document.getElementById("empty").hidden = has;
    if (!has) return;
    var tone = function (s) { s = String(s || "").toLowerCase(); return /down|fail|bad|error|red/.test(s) ? "bad" : /warn|slow|degrad|amber|yellow/.test(s) ? "warn" : "ok"; };
    var bad = items.filter(function (x) { return tone(x.status) !== "ok"; }).length;
    document.getElementById("sum").textContent = bad ? bad + " of " + items.length + " need a look" : "All " + items.length + " fine";
    document.getElementById("rows").innerHTML = items.map(function (x, i) {
      return '<div class="cb-item cb-in" style="--i:' + i + '"><span class="cb-dot is-' + tone(x.status) + '"></span><span class="cb-item-label"><span class="cb-item-title">' + board.esc(x.label || "") + "</span>" + (x.sub ? '<span class="cb-item-sub">' + board.esc(x.sub) + "</span>" : "") + '</span><span class="cb-item-value">' + board.esc(x.status || "") + "</span></div>";
    }).join("");
  });
</script>`;
}

function feed(max: number): string {
  return `<div class="cb-fill" id="main" hidden>
  <div class="cb-scroll cb-grow"><div class="cb-list" id="rows"></div></div>
</div>
<div class="cb-empty" id="empty"><b>Nothing yet</b>Items show as soon as its data sends them.</div>
<script>
  // how many items it shows
  var MAX = ${js(max)};
  board.onData(function (d) {
    var items = (d.items || []).slice(0, MAX);
    var has = items.length > 0;
    document.getElementById("main").hidden = !has;
    document.getElementById("empty").hidden = has;
    if (!has) return;
    document.getElementById("rows").innerHTML = items.map(function (x, i) {
      var meta = [typeof x.value === "number" ? board.fmt(x.value) : "", x.sub || "", x.t ? board.ago(x.t) : ""].filter(Boolean).join(" · ");
      var title = board.esc(x.label || "");
      return '<div class="cb-item cb-in" style="--i:' + i + '"><span class="cb-item-label">' + (x.href ? '<a class="cb-item-title" href="' + board.esc(x.href) + '">' + title + "</a>" : '<span class="cb-item-title">' + title + "</span>") + (meta ? '<span class="cb-item-sub">' + board.esc(meta) + "</span>" : "") + "</span></div>";
    }).join("");
  });
</script>`;
}

function brief(): string {
  return `<div class="cb-fill" id="main" hidden>
  <div class="cb-scroll cb-grow" id="text"></div>
  <span class="cb-faint" id="when"></span>
</div>
<div class="cb-empty" id="empty"><b>Nothing written yet</b>It shows as soon as its data sends text.</div>
<style>
  #text { font-size: 0.95rem; line-height: 1.55; color: var(--fg); }
  #text h3 { font-size: 1.05rem; margin: 0.8rem 0 0.3rem; }
  #text ul { margin: 0.3rem 0; padding-left: 1.2rem; }
  #text p { margin: 0 0 0.55rem; }
</style>
<script>
  // a little Markdown: headings, lists, bold and paragraphs
  function inline(s) { return board.esc(s).replace(/\\*\\*(.+?)\\*\\*/g, "<b>$1</b>").replace(/\`(.+?)\`/g, "<code>$1</code>"); }
  function markdown(text) {
    var out = [], list = false;
    text.split("\\n").forEach(function (line) {
      var m;
      if ((m = /^\\s*[-*]\\s+(.*)$/.exec(line))) { if (!list) { out.push("<ul>"); list = true; } out.push("<li>" + inline(m[1]) + "</li>"); return; }
      if (list) { out.push("</ul>"); list = false; }
      if ((m = /^#{1,6}\\s+(.*)$/.exec(line))) out.push("<h3>" + inline(m[1]) + "</h3>");
      else if (line.trim()) out.push("<p>" + inline(line) + "</p>");
    });
    if (list) out.push("</ul>");
    return out.join("");
  }
  board.onData(function (d) {
    var has = typeof d.text === "string" && d.text.trim().length > 0;
    document.getElementById("main").hidden = !has;
    document.getElementById("empty").hidden = has;
    if (!has) return;
    document.getElementById("text").innerHTML = markdown(d.text);
    document.getElementById("when").textContent = d.updatedAt ? "Written " + board.ago(d.updatedAt) : "";
  });
</script>`;
}
